// Port of webapp/src/blocks/board.ts

import {Utils, IDType} from '../common/utils'
import {Constants} from '../common/constants'
import {Block} from './block'
import {Card} from './card'

export const BoardTypeOpen = 'O'
export const BoardTypePrivate = 'P'
export const boardTypes = [BoardTypeOpen, BoardTypePrivate] as const
export type BoardTypes = typeof boardTypes[number]

export enum MemberRole {
    Viewer = 'viewer',
    Commenter = 'commenter',
    Editor = 'editor',
    Admin = 'admin',
    None = '',
}

export type PropertyTypeEnum =
    | 'text' | 'number' | 'select' | 'multiSelect' | 'date' | 'person' | 'multiPerson'
    | 'file' | 'checkbox' | 'url' | 'email' | 'phone' | 'createdTime' | 'createdBy'
    | 'updatedTime' | 'updatedBy' | 'unknown'

export interface IPropertyOption {
    id: string
    value: string
    color: string
}

/** A template for card properties attached to a board */
export interface IPropertyTemplate {
    id: string
    name: string
    type: PropertyTypeEnum
    options: IPropertyOption[]
}

export type Board = {
    id: string
    teamId: string
    channelId?: string
    createdBy: string
    modifiedBy: string
    type: BoardTypes
    minimumRole: MemberRole

    title: string
    description: string
    icon?: string
    showDescription: boolean
    isTemplate: boolean
    templateVersion: number
    properties: Record<string, string | string[]>
    cardProperties: IPropertyTemplate[]

    createAt: number
    updateAt: number
    deleteAt: number
}

export type BoardPatch = {
    type?: BoardTypes
    minimumRole?: MemberRole
    title?: string
    description?: string
    icon?: string
    showDescription?: boolean
    updatedProperties?: Record<string, any>
    deletedProperties?: string[]
    updatedCardProperties?: IPropertyTemplate[]
    deletedCardProperties?: string[]
}

export type BoardMember = {
    boardId: string
    userId: string
    roles?: string
    minimumRole: MemberRole
    schemeAdmin: boolean
    schemeEditor: boolean
    schemeCommenter: boolean
    schemeViewer: boolean
    synthetic: boolean
}

export type BoardsAndBlocks = {
    boards: Board[]
    blocks: Block[]
}

export type BoardGroup = {
    option: IPropertyOption
    cards: Card[]
}

export function createBoard(board?: Partial<Board>): Board {
    const now = Date.now()
    const cardProperties = (board?.cardProperties || []).map((o) => ({
        id: o.id,
        name: o.name,
        type: o.type,
        options: o.options ? o.options.map((option) => ({...option})) : [],
    }))

    return {
        id: board?.id || Utils.createGuid(IDType.Board),
        teamId: board?.teamId || '',
        channelId: board?.channelId || '',
        createdBy: board?.createdBy || '',
        modifiedBy: board?.modifiedBy || '',
        type: board?.type || BoardTypePrivate,
        minimumRole: board?.minimumRole || MemberRole.None,
        title: board?.title || '',
        description: board?.description || '',
        icon: board?.icon || '',
        showDescription: board?.showDescription || false,
        isTemplate: board?.isTemplate || false,
        templateVersion: board?.templateVersion || 0,
        properties: board?.properties || {},
        cardProperties,
        createAt: board?.createAt || now,
        updateAt: board?.updateAt || now,
        deleteAt: board?.deleteAt || 0,
    }
}

/**
 * Upstream diffs a board against its previous value to emit
 * [patch, undoPatch] pairs; the mock server replays the same shape.
 */
export function createPatchesFromBoards(newBoard: Board, oldBoard: Board): [BoardPatch, BoardPatch] {
    const build = (next: Board, prev: Board): BoardPatch => {
        const updatedProperties: Record<string, any> = {}
        Object.keys(next.properties || {}).forEach((key) => {
            if ((prev.properties || {})[key] !== next.properties[key]) {
                updatedProperties[key] = next.properties[key]
            }
        })
        const updatedData: Record<string, any> = {}
        Object.keys(next).forEach((key) => {
            if (key !== 'properties' && key !== 'cardProperties' && (next as any)[key] !== (prev as any)[key]) {
                updatedData[key] = (next as any)[key]
            }
        })
        const deletedProperties = Object.keys(prev.properties || {}).filter((k) => !(k in (next.properties || {})))
        const updatedCardProperties = next.cardProperties.filter((p) => {
            const old = prev.cardProperties.find((o) => o.id === p.id)
            return !old || JSON.stringify(old) !== JSON.stringify(p)
        })
        const deletedCardProperties = next.cardProperties.filter((p) => !prev.cardProperties.find((o) => o.id === p.id)).map((p) => p.id)

        return {
            ...updatedData,
            updatedProperties,
            deletedProperties,
            updatedCardProperties,
            deletedCardProperties,
        } as BoardPatch
    }
    return [build(newBoard, oldBoard), build(oldBoard, newBoard)]
}

export function findPropertyTemplate(board: Board, propertyId: string): IPropertyTemplate | undefined {
    if (propertyId === Constants.titleColumnId) {
        return {id: Constants.titleColumnId, name: '标题', type: 'text', options: []}
    }
    return board.cardProperties.find((o) => o.id === propertyId)
}
